import { supabase } from '@/lib/supabase/client'
import { Tables } from '@/types/database'
import { logger } from '@/lib/utils/logger'
import { sessionCache } from '@/lib/auth/NewSessionCache'
import { apiFetch } from '@/lib/api/client'

type Profile = Tables<'user_profiles'>
import { User } from '@supabase/supabase-js'

const AUTH_RETURN_PATH_COOKIE = 'auth_return_path'

interface EmailSignUpData {
  email: string
  password: string
  firstName: string
  lastName: string
}


interface ProfileData {
  firstName?: string
  lastName?: string
}

class AuthService {
  // Use the singleton supabase client to ensure session state is shared across all consumers
  private supabase = supabase

  /**
   * Clear stale auth cookies and cache without going through supabase.auth.signOut(),
   * which resets GoTrue internal state/abort controllers and can abort the next request.
   */
  private clearStaleAuthState() {
    // Clear stale session cookie chunks (but NOT code-verifier – PKCE needs it)
    document.cookie.split(';').forEach(c => {
      const name = c.trim().split('=')[0]
      if (name.startsWith('sb-') && name.includes('-auth-token') && !name.includes('code-verifier')) {
        document.cookie = `${name}=;path=/;max-age=0`
      }
    })
    sessionCache.clear()
  }

  /**
   * Save the return path as a cookie so the server-side callback route can read it.
   * This is a fallback for when Supabase strips the ?next= query parameter
   * during OAuth or Magic Link redirect chains.
   */
  private saveReturnPath(path?: string) {
    if (path && path !== '/' && path !== '/dashboard') {
      document.cookie = `${AUTH_RETURN_PATH_COOKIE}=${encodeURIComponent(path)};path=/;max-age=600;SameSite=Lax`
    }
  }

  async signUpWithEmail(data: EmailSignUpData) {
    const { data: authData, error: authError } = await this.supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
          auth_provider: 'email'
        }
      }
    })

    if (authError) {
      throw new Error(`Email sign up failed: ${authError.message}`)
    }

    return authData
  }


  async signInWithEmail(email: string, password: string) {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      throw new Error(`Email sign in failed: ${error.message}`)
    }

    return data
  }


  async sendMagicLink(email: string, userOptions?: { data?: Record<string, unknown> }, redirectTo?: string) {
    this.saveReturnPath(redirectTo)
    // emailRedirectTo MUST be an absolute URL. GoTrue's IsRedirectURLValid
    // requires a parseable hostname; relative paths fail validation and
    // {{ .RedirectTo }} silently falls back to the Referer/SiteURL, dropping
    // the destination. The email template embeds this as &next= for the
    // /api/auth/callback route, which extracts the path on arrival.
    const path = redirectTo || '/dashboard'
    const emailRedirectTo = path.startsWith('http')
      ? path
      : `${window.location.origin}${path}`
    // Clear stale auth state to prevent cookie conflicts (Samsung Internet, etc.)
    this.clearStaleAuthState()

    const { data, error } = await this.supabase.auth.signInWithOtp({
      email,
      options: {
        ...userOptions,
        emailRedirectTo,
      }
    })

    if (error) {
      throw new Error(`Magic link failed: ${error.message}`)
    }

    return data
  }

  async verifyOtp(email: string, token: string, isNewUser: boolean) {
    // GoTrue creates different token types based on user state:
    // - Existing confirmed user (signInWithOtp) → recovery_token → verify with 'magiclink'
    // - New/unconfirmed user (signInWithOtp + shouldCreateUser) → confirmation_token → verify with 'signup'
    const primaryType = isNewUser ? 'signup' : 'magiclink'
    const fallbackType = isNewUser ? 'magiclink' : 'signup'

    const { data, error } = await this.supabase.auth.verifyOtp({
      email,
      token,
      type: primaryType
    })

    if (error) {
      // If primary type fails, try the other type – auth.users state may not
      // match our expectation (e.g. profile missing but auth user exists)
      logger.warn(
        `[Auth] OTP verification fallback triggered for ${email}: ` +
        `primary type "${primaryType}" failed (${error.message}), trying "${fallbackType}"`
      )

      const { data: fallbackData, error: fallbackError } = await this.supabase.auth.verifyOtp({
        email,
        token,
        type: fallbackType
      })

      if (fallbackError) {
        throw new Error(`OTP verification failed: ${error.message}`)
      }

      logger.warn(
        `[Auth] OTP fallback succeeded for ${email} with type "${fallbackType}" – ` +
        `auth.users state may be out of sync with isNewUser=${isNewUser}`
      )

      return fallbackData
    }

    return data
  }

  async signInWithGoogle(redirectTo?: string) {
    this.saveReturnPath(redirectTo)
    const callbackUrl = `${window.location.origin}/api/auth/callback`
    const redirectUrl = redirectTo
      ? `${callbackUrl}?next=${encodeURIComponent(redirectTo)}`
      : callbackUrl
    // Clear stale auth state to prevent cookie conflicts (Samsung Internet, etc.)
    this.clearStaleAuthState()

    const { data, error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      }
    })

    if (error) {
      throw new Error(`Google sign in failed: ${error.message}`)
    }

    return data
  }

  /**
   * Supabase defaults to scope 'global', which destroys every session the user
   * has on every device. Default to 'local' so signing out on one device is
   * just that; 'global' stays available for the explicit "sign out everywhere"
   * action on the profile page.
   */
  async signOut(scope: 'local' | 'global' = 'local') {
    const { error } = await this.supabase.auth.signOut({ scope })

    if (error && error.message !== 'Auth session missing!') {
      throw new Error(`Sign out failed: ${error.message}`)
    }
  }

  async getCurrentUser() {
    const { data: { user }, error } = await this.supabase.auth.getUser()

    if (error) {
      throw new Error(`Failed to get current user: ${error.message}`)
    }

    return user
  }

  async checkProfileExists(email: string) {
    try {
      const response = await fetch('/api/auth/check-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to check profile')
      }

      const result = await response.json()
      // Return format: { data: { id: string } } or { data: null }
      return { data: result.exists ? { id: result.profileId } : null }
    } catch (error) {
      logger.error('[Auth Service] Unexpected error checking profile:', error)
      throw error
    }
  }

  /**
   * Own profile, read through /api/profile. The route authenticates from the
   * session cookie, so this read no longer depends on the browser client's
   * auth Web Lock (2026-09-17). `userId` is the caller's expectation and is
   * checked against the row that came back.
   */
  async getProfile(userId: string): Promise<Profile | null> {
    const { profile } = await apiFetch<{ profile: Profile | null }>('/api/profile')
    if (!profile) return null
    if (profile.id !== userId) {
      logger.warn('[Auth Service] /api/profile returned a row for another user; ignoring')
      return null
    }
    return profile
  }

  async updateProfile(userId: string, profileData: ProfileData) {
    // Get the current user to get their email
    const { data: { user } } = await this.supabase.auth.getUser()

    // medical_license_number and organization are not user_profiles columns -
    // this upsert only ever "worked" because they came through as undefined
    // and Supabase silently drops undefined keys. specialty is now derived
    // server-side by a DB trigger from specialty_id and must not be written
    // directly here either.
    const updateData = {
      id: userId,
      email: user?.email || '',
      first_name: profileData.firstName || '',
      last_name: profileData.lastName || '',
      updated_at: new Date().toISOString(),
    }

    const { data, error } = await this.supabase
      .from('user_profiles')
      .upsert(updateData)
      .select()
      .returns<Profile>()
      .single()

    if (error) {
      throw new Error(`Failed to update profile: ${error.message}`)
    }

    return data
  }

  async sendPasswordReset(email: string) {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })

    if (error) {
      throw new Error(`Failed to send password reset: ${error.message}`)
    }
  }

  async updatePassword(newPassword: string) {
    const { error } = await this.supabase.auth.updateUser({
      password: newPassword,
    })

    if (error) {
      throw new Error(`Failed to update password: ${error.message}`)
    }
  }

  async updateEmail(newEmail: string) {
    const { data, error } = await this.supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: `${window.location.origin}/api/auth/callback?type=email_change` }
    )

    if (error) {
      throw new Error(`Failed to initiate email change: ${error.message}`)
    }

    return data
  }

  // Listen to auth state changes
  onAuthStateChange(callback: (user: User | null) => void) {
    return this.supabase.auth.onAuthStateChange((event, session) => {
      callback(session?.user || null)
    })
  }
}

export const authService = new AuthService()

/**
 * Clear all Supabase auth cookies and localStorage session cache.
 * Used as a self-service recovery when the session is corrupted
 * (e.g. after traffic spikes or stale cookie chunks).
 */
export function clearAuthSession(): void {
  // Clear all sb-* auth cookies
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim()
    if (name.startsWith('sb-') && name.includes('-auth-token')) {
      document.cookie = `${name}=; path=/; max-age=0`
    }
  })

  // Clear localStorage session cache
  try {
    localStorage.removeItem('auth_session_cache')
    // Also clear any profile caches
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('auth_profile_cache_')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch {
    // Ignore storage errors
  }
}
